import {
	BillToIsInactiveText,
	BillToTitle,
	NetTotalText,
	NoActiveBillTosText,
	NoChargeOrder,
	SeePricingInfoText,
	SubtotalText,
	UnavailableDataPlaceholderText
} from "constants/text";
import {
	BillToInactiveHelperContainer,
	BillToShipToBoxWrapper,
	PricingContainer,
	PricingDefinitionButton
} from "../BillToShipToBox.styles";
import { convertToTitleCase, formatPriceWithPrefix } from "utils/string";
import { AcknowledgementPriceFormat, AcknowledgementPriceFormatType, OrderType } from "types/declarations/enums.ts";
import { models } from "types/api/viewModels.ts";
import { ReactNode, useContext, useState } from "react";
import HelperText from "components/Form/HelperText/HelperText";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import { InfoIcon } from "components/Trucks/TruckDetailsFlyout/TruckDetailsFlyout.styles";
import PricingDefinitionModal from "components/Acknowledgements/PricingDefinitionModal/PricingDefinitionModal";
import {
	ListPricingDefinitions,
	NetPricingDefinitions
} from "components/Acknowledgements/PricingDefinitionModal/pricingDefinitions";
import { useGetPermissionsQuery } from "features/api/permissionsApi.ts";

interface BillToBoxProps {
	className?: string;
	billTo: models["BillToViewModel"] | null | undefined;
	orderType: string | undefined;
	orderDetail?: models["OrderDetailViewModel"];
	isNoChargeOrder?: boolean;
	orderDetailSubTotal?: number | null;
	orderDetailNetTotal?: number | null;
	ackPriceFormatOverride?: AcknowledgementPriceFormatType | null;
	hasBorder?: boolean;
	hasMinHeight?: boolean;
	dataTestIdPrefix: string;
	containsSubtotal?: boolean;
	billToController?: ReactNode;
	hasActiveBillTos?: boolean;
	billToSelectedIsActive?: boolean;
}

const BillToBox = ({
	className,
	billTo,
	orderType,
	orderDetail,
	isNoChargeOrder = false,
	orderDetailSubTotal = null,
	orderDetailNetTotal = null,
	ackPriceFormatOverride = null,
	hasBorder = false,
	hasMinHeight = false,
	dataTestIdPrefix,
	containsSubtotal = true,
	billToController,
	hasActiveBillTos,
	billToSelectedIsActive
}: BillToBoxProps) => {
	const cityState = [billTo?.city, billTo?.state].filter(Boolean).join(", ");
	const { configurationsData } = useContext(ConfigurationsContext);
	const { data: permissionsData } = useGetPermissionsQuery();
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const ackPricePermission = ackPriceFormatOverride ?? permissionsData?.acknowledgementPriceFormat;
	const isNetPrice = ackPricePermission !== AcknowledgementPriceFormat.LIST || billTo?.cashInAdvance;
	const showAckPricingInfo =
		configurationsData?.featureFlags?.acknowledgementsEnabled && orderDetail?.isAcknowledgementEligible;
	const showSubtotal = orderType !== OrderType.BACKORDER && !isNoChargeOrder && orderDetailSubTotal && !isNetPrice;
	const showNetTotal =
		configurationsData?.featureFlags?.acknowledgementsEnabled &&
		!isNoChargeOrder &&
		orderDetailNetTotal !== null &&
		isNetPrice;
	const isReplacementOrder = orderType === OrderType.REPLACEMENT;
	const showNoChargeOrder = isReplacementOrder || isNoChargeOrder;

	const toggleModal = () => {
		setIsModalOpen((prev) => !prev);
	};

	return (
		<BillToShipToBoxWrapper
			className={className}
			hasBorder={hasBorder}
			hasMinHeight={hasMinHeight}
			hasSubtotal={containsSubtotal}
			isInactiveBillTo={!!billToController && !billToSelectedIsActive}
			hasDropdown={!!billToController}
			showAckPricingInfo={showAckPricingInfo}
		>
			<h6>{BillToTitle}</h6>
			{billToController && !billToSelectedIsActive && (
				<BillToInactiveHelperContainer>
					<HelperText
						isError
						text={hasActiveBillTos ? BillToIsInactiveText : NoActiveBillTosText}
					/>
				</BillToInactiveHelperContainer>
			)}
			<PricingContainer>
				{billToController ?? (
					<div>
						{billTo?.projectName && billTo.projectName.trim() !== "" && (
							<p data-testid={dataTestIdPrefix + "-bill-to-projectName"}>{billTo.projectName} -</p>
						)}
						<p data-testid={dataTestIdPrefix + "-bill-to-city-state-id"}>
							{cityState ? `${cityState} - ` : UnavailableDataPlaceholderText}
							{billTo?.logisticsMode && `${convertToTitleCase(billTo.logisticsMode)} - `}
							{billTo?.billToId}
						</p>
					</div>
				)}
				{containsSubtotal && (
					<div style={{ textAlign: "right" }}>
						{Boolean(showSubtotal && !isReplacementOrder) && (
							<div>
								<b>{SubtotalText}</b>
								<p data-testid={dataTestIdPrefix + "-billToSubtotal"}>
									{formatPriceWithPrefix(orderDetailSubTotal)}
								</p>
							</div>
						)}

						{showNetTotal && !isReplacementOrder && (
							<div>
								<b>{NetTotalText}</b>
								<p data-testid={dataTestIdPrefix + "-billToNetTotal"}>
									{formatPriceWithPrefix(orderDetailNetTotal, false, true)}
								</p>
							</div>
						)}

						{showNoChargeOrder && (
							<b data-testid={dataTestIdPrefix + "-no-charge-order"}>{NoChargeOrder}</b>
						)}
					</div>
				)}
			</PricingContainer>
			{showAckPricingInfo && (
				<>
					<PricingDefinitionButton
						variant="text"
						onClick={toggleModal}
					>
						<InfoIcon />
						{SeePricingInfoText}
					</PricingDefinitionButton>
					<PricingDefinitionModal
						pricingDefinitions={isNetPrice ? NetPricingDefinitions : ListPricingDefinitions}
						isOpen={isModalOpen}
						toggleModal={toggleModal}
						dataTestIdPrefix={isNetPrice ? "net" : "list"}
					/>
				</>
			)}
		</BillToShipToBoxWrapper>
	);
};

export default BillToBox;
